<template>
  <v-card flat>
    <v-dialog v-model="showFilters" max-width="640" scrollable>
      <v-card data-cy="photos-filter-dialog">
        <v-card-title>
          Filters
          <v-spacer/>
          <v-btn icon @click="showFilters = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center">
            <v-btn icon title="Previous week" @click="moveWeek(-1)" data-cy="photos-prev-week">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  class="week-field"
                  :value="date"
                  label="Week (Sunday)"
                  prepend-icon="mdi-calendar"
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  data-cy="photos-week"
                />
              </template>
              <v-date-picker
                v-model="date"
                no-title
                scrollable
                :allowed-dates="isSunday"
                @change="dateMenu = false"
              />
            </v-menu>
            <v-btn icon title="Next week" @click="moveWeek(1)" data-cy="photos-next-week">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-select
              class="weeks-select ml-4"
              label="Weeks"
              v-model="weekCount"
              :items="weekCountItems"
              hide-details
              data-cy="photos-weeks"
            />
          </div>
          <v-select
            class="mt-4"
            label="Streams"
            v-model="streamFilter"
            :items="streamItems"
            multiple
            clearable
            small-chips
            hide-details
            data-cy="photos-streams"
          />
          <v-select
            class="mt-4"
            label="Categories"
            v-model="categoryFilter"
            :items="categoryItems"
            multiple
            clearable
            small-chips
            hide-details
            data-cy="photos-categories"
          />
          <v-select
            class="mt-4"
            label="Photo"
            v-model="slotFilter"
            :items="slotItems"
            multiple
            clearable
            small-chips
            hide-details
            data-cy="photos-slots"
          />
          <allergy-select
            class="mt-4"
            label="Allergies (hide meals containing)"
            v-model="allergyFilter"
            data-cy="photos-allergies"
          />
          <p class="text-caption grey--text mt-n2 mb-0">A meal with no ingredient tags cannot be checked and stays.</p>
          <v-select
            class="mt-4"
            label="Meal preset"
            v-model="presetFilter"
            :items="presetItems"
            clearable
            hide-details
            hint="Only meals that meet the preset's nutrient limits (a day's, over three meals) and restrictions"
            persistent-hint
            data-cy="photos-preset-filter"
          />
          <v-text-field
            class="mt-2"
            label="Search name, id or ingredients"
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            data-cy="photos-search"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="clearFilters" :disabled="activeFilterCount === 0" data-cy="photos-clear-filters">Clear filters</v-btn>
          <v-btn text @click="toggleTopNav" data-cy="photos-toggle-nav">{{ hideTopNav ? 'Show menu' : 'Hide menu' }}</v-btn>
          <v-spacer/>
          <span class="text-caption grey--text mr-2">{{ shownPhotos.length }} of {{ photos.length }} photos</span>
          <v-btn text color="primary" @click="showFilters = false">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showQuality" max-width="640">
      <v-card data-cy="photos-quality-dialog">
        <v-card-title>
          Quality
          <v-spacer/>
          <v-btn icon @click="showQuality = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-caption mb-2">
            The catalog publishes the <code>small</code> variant of each Cloudflare image. Pick another named
            variant, or set resize options for a flexible one; resizes never upscale, so a big width at q=100
            is the original.
          </p>
          <div class="d-flex align-center flex-wrap mb-2">
            <v-chip-group :value="preset" mandatory class="quick-presets mr-2" data-cy="photos-quick-presets">
              <v-chip
                v-for="quick of quickPresets"
                :key="quick.key"
                :value="quick.key"
                small
                outlined
                :color="preset === quick.key ? 'primary' : undefined"
                @click="applyPreset(quick.key)"
              >
                {{ quick.short }}
              </v-chip>
            </v-chip-group>
            <span v-if="imageSize" class="text-caption grey--text text-no-wrap" data-cy="photos-resolution">now {{ imageSize.width }}×{{ imageSize.height }}</span>
          </div>
          <v-row dense>
            <v-col cols="6" sm="3">
              <v-text-field label="Width" v-model="imageOptions.width" type="number" dense hide-details data-cy="photos-width"/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field label="Height" v-model="imageOptions.height" type="number" dense hide-details/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field label="Quality" v-model="imageOptions.quality" type="number" min="1" max="100" dense hide-details data-cy="photos-quality"/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field label="Sharpen" v-model="imageOptions.sharpen" type="number" min="0" max="10" dense hide-details/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-select label="Fit" v-model="imageOptions.fit" :items="fitItems" clearable dense hide-details/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-select label="Format" v-model="imageOptions.format" :items="formatItems" clearable dense hide-details/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field label="Blur" v-model="imageOptions.blur" type="number" min="0" max="250" dense hide-details/>
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field label="DPR" v-model="imageOptions.dpr" type="number" min="1" max="3" dense hide-details/>
            </v-col>
          </v-row>
          <p class="text-caption mt-4 mb-1" data-cy="photos-variant-explanation">{{ variantExplanation }}</p>
          <v-text-field
            label="Image url"
            :value="displayUrl"
            readonly
            dense
            outlined
            hide-details
            append-icon="mdi-content-copy"
            @click:append="copy(displayUrl, 'Link copied')"
            data-cy="photos-url"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary" @click="showQuality = false">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-progress-linear v-if="isLoading" indeterminate color="primary"/>
    <v-card-text v-if="error" class="pb-0">
      <v-alert type="warning" dense>{{ error }}</v-alert>
    </v-card-text>
    <v-card-text v-if="weeksWithoutMenu.length" class="pb-0">
      <v-alert type="info" dense text>
        No menu has been published for {{ weeksWithoutMenu.join(', ') }}
      </v-alert>
    </v-card-text>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="8">
          <div v-if="!selected" class="stage stage-empty" data-cy="photos-empty">
            <span v-if="!isLoading">{{ photos.length ? 'No photos match the filters' : 'No photos on these menus' }}</span>
          </div>
          <div v-else ref="stage" class="stage" :class="{'stage-fullscreen': isFullscreen, 'stage-zoomed': isZoomed}" tabindex="0">
            <v-img
              ref="large"
              :key="displayUrl"
              :src="displayUrl"
              :alt="selected.name"
              :aspect-ratio="imageAspectRatio"
              :width="imageBoxWidth"
              contain
              class="stage-image"
              @click.native="isZoomed = !isZoomed"
              @load="onLargeLoad"
              data-cy="photos-large"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-2"/>
                </v-row>
              </template>
              <div class="stage-tools">
                <v-btn icon class="stage-tool" :class="{'stage-tool-on': isZoomed}" @click.stop="isZoomed = !isZoomed" :title="isZoomed ? 'Back to the whole photo' : 'Zoom in on the centre of the photo'" data-cy="photos-zoom">
                  <v-icon>{{ isZoomed ? 'mdi-magnify-minus' : 'mdi-magnify-plus' }}</v-icon>
                </v-btn>
                <v-btn icon class="stage-tool" @click.stop="fullscreen" title="Fullscreen" data-cy="photos-fullscreen">
                  <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                </v-btn>
              </div>
              <div v-if="isFullscreen" class="stage-caption">
                {{ selected.name }} (M{{ selected.mealId }}) · {{ selected.slot }} · {{ selectedIndex + 1 }} / {{ shownPhotos.length }}
              </div>
            </v-img>
            <v-chip small label class="stage-quality" data-cy="photos-quality-summary">{{ qualitySummary }}</v-chip>
            <div class="stage-title" data-cy="photos-stage-title">
              <v-chip small label class="stage-title-chip">{{ selected.name }}</v-chip>
              <v-chip v-for="stream of visibleStreams(selected)" :key="stream" small label class="stage-title-chip">{{ streamLabel(stream) }}</v-chip>
            </div>
            <v-btn class="stage-nav stage-prev" icon large :disabled="selectedIndex <= 0" @click="step(-1)" title="Previous photo (←)">
              <v-icon large>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn class="stage-nav stage-next" icon large :disabled="selectedIndex >= shownPhotos.length - 1" @click="step(1)" title="Next photo (→)">
              <v-icon large>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <div v-if="selected" class="stage-footer" data-cy="photos-footer">
            <div class="d-flex flex-nowrap align-center button-row">
              <v-btn outlined small class="mr-2" @click="copy(displayUrl, 'Link copied')" title="Copy the image link" data-cy="photos-copy-link">
                <v-icon left small>mdi-link-variant</v-icon>
                Link
              </v-btn>
              <v-btn outlined small class="mr-2" @click="copy(detailsLine, 'Link and details copied')" title="Copy the link with the meal details" data-cy="photos-copy-details">
                <v-icon left small>mdi-content-copy</v-icon>
                Link + details
              </v-btn>
              <v-btn outlined small class="mr-2" :loading="isDownloading" @click="download" title="Download the image" data-cy="photos-download">
                <v-icon left small>mdi-download</v-icon>
                Download
              </v-btn>
              <v-btn outlined small class="mr-2" @click="showQuality = true" title="Image quality options" data-cy="photos-quality-button">
                <v-icon left small>mdi-tune</v-icon>
                Quality
              </v-btn>
              <v-spacer/>
              <span class="text-caption grey--text text-no-wrap mr-2" data-cy="photos-position">
                {{ selectedIndex + 1 }} of {{ shownPhotos.length }}
              </span>
              <v-btn outlined small :color="showDetails ? 'primary' : undefined" @click="showDetails = !showDetails" :title="showDetails ? 'Hide the meal details' : 'Show the meal details'" data-cy="photos-details-button">
                <v-icon left small>{{ showDetails ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                Details
              </v-btn>
            </div>
            <v-expand-transition>
            <v-card v-show="showDetails" outlined class="mt-2 stage-details" data-cy="photos-details">
              <v-card-title class="pb-1">
                {{ selected.name }}
                <span class="ml-2 text-subtitle-1 grey--text">M{{ selected.mealId }}</span>
                <v-chip class="ml-2" small outlined>{{ selected.slot }}</v-chip>
                <v-spacer/>
                <span v-if="selected.price" class="text-subtitle-1">{{ formatCurrency(selected.price) }}</span>
              </v-card-title>
              <v-card-text>
                <div class="mb-1">
                  <span class="grey--text mr-2">Streams</span>
                  <v-chip v-for="stream of visibleStreams(selected)" :key="stream" small class="mr-1 mb-1">
                    {{ streamLabel(stream) }}
                  </v-chip>
                </div>
                <div class="mb-1">
                  <span class="grey--text mr-2">Categories</span>
                  <v-chip v-for="category of selected.categories" :key="category" small outlined class="mr-1 mb-1">
                    {{ category }}
                  </v-chip>
                </div>
                <div class="mb-1">
                  <span class="grey--text mr-2">Dates</span>
                  <template v-for="date of selected.dates">
                    <v-chip :key="date" small outlined class="mr-1 mb-1">{{ date === 'any' ? "chef's choice" : dateFormatted(date) }}</v-chip>
                  </template>
                </div>
                <div v-if="selected.sku" class="mb-1"><span class="grey--text mr-2">SKU</span>{{ selected.sku }}</div>
                <div v-if="selected.ingredients" class="mb-1"><span class="grey--text mr-2">Ingredients</span>{{ selected.ingredients }}</div>
                <v-chip-group v-if="selected.tags.length" column>
                  <v-chip v-for="tag of selected.tags" :key="tag" x-small outlined>{{ tag }}</v-chip>
                </v-chip-group>

                <v-text-field
                  class="mt-3"
                  label="Copy link + details"
                  :value="detailsLine"
                  readonly
                  dense
                  outlined
                  hide-details
                  append-icon="mdi-content-copy"
                  @click:append="copy(detailsLine, 'Link and details copied')"
                  data-cy="photos-details-line"
                />
                <v-text-field
                  class="mt-3"
                  label="Image url"
                  :value="displayUrl"
                  readonly
                  dense
                  outlined
                  hide-details
                  append-icon="mdi-content-copy"
                  @click:append="copy(displayUrl, 'Link copied')"
                />
                <div v-if="displayUrl !== selected.url" class="text-caption grey--text mt-1">
                  Published: {{ selected.url }}
                </div>
              </v-card-text>
            </v-card>
            </v-expand-transition>
          </div>
        </v-col>

        <v-col cols="12" md="4" class="thumb-pane">
          <div class="d-flex align-center mb-2">
            <v-badge :content="activeFilterCount" :value="activeFilterCount > 0" color="primary" overlap>
              <v-btn outlined small @click="showFilters = true" data-cy="photos-filter-button">
                <v-icon left small>mdi-filter-variant</v-icon>
                Filters
              </v-btn>
            </v-badge>
            <span class="text-caption grey--text text--darken-1 ml-3" data-cy="photos-filter-summary">{{ filterSummary }}</span>
          </div>
          <template v-for="group of thumbnailGroups">
            <h4 v-if="weeks.length > 1" :key="`h-${group.week}`" class="mt-2 mb-1">Week of {{ dateFormatted(group.week) }}</h4>
            <v-row :key="group.week" class="thumb-row">
              <v-col
                v-for="photo of group.photos"
                :key="photo.key"
                cols="4"
                class="thumb-col"
              >
                <div
                  class="thumb"
                  :class="{'thumb-selected': selected && photo.key === selected.key}"
                  @click="select(photo)"
                  :title="`${photo.name} (M${photo.mealId}) · ${photo.slot}`"
                  data-cy="photos-thumb"
                >
                  <v-img :src="thumbnailUrl(photo.url)" :aspect-ratio="3/2" :alt="photo.name">
                    <div class="thumb-chips">
                      <v-chip x-small label class="thumb-chip">{{ photo.isChefChoice ? "chef's choice" : shortDate(photo.dates[0]) }}</v-chip>
                      <v-chip x-small label class="thumb-chip">{{ photo.slot }}</v-chip>
                    </div>
                  </v-img>
                  <div class="thumb-caption">{{ photo.name }}</div>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import api from '@/api';
import urlState from '@/router/urlState';
import AllergySelect from '@/components/AllergySelect';
import {dateFormatted, formatCurrency, getNextOrderingDate} from '@/store/utils';
import {
  collectPhotos,
  distinctValues,
  filterPhotos,
  mealFilterCheck,
  PHOTO_SLOTS,
  photoDetailsLine,
  photoFileName,
  restrictedTagIds,
  visibleStreams,
} from '@/utils/meal-photos';
import {
  cloudflareImageUrl,
  FIT_OPTIONS,
  flexibleVariant,
  FORMAT_OPTIONS,
  isNamedVariant,
  NAMED_VARIANTS,
  parseCloudflareImage,
  parseFlexibleVariant,
  PRESETS,
  thumbnailUrl,
} from '@/utils/cloudflare-image';

const MAX_WEEKS = 8;

// the filters and the image variant are kept between visits; a link's own query wins over them
const FILTERS_KEY = 'meal-photos-filters-v1';
const FILTER_PARAMS = {weekCount: 'weeks', streamFilter: 'stream', categoryFilter: 'category', slotFilter: 'slot', allergyFilter: 'allergy', presetFilter: 'preset', variant: 'variant'};

function loadSavedFilters() {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function emptyImageOptions() {
  return {width: '', height: '', quality: '', fit: null, format: null, sharpen: '', blur: '', dpr: ''};
}

const toList = value => (Array.isArray(value) ? value : (value ? [value] : []));

export default {
  name: 'MealPhotos',
  components: {AllergySelect},
  mixins: [urlState],
  data() {
    return {
      date: getNextOrderingDate(),
      dateMenu: false,
      weekCount: 1,
      // menus fetched so far, by week: null for a week with no published menu
      menusByWeek: {},
      // weeks whose fetch is in flight, so a filter change does not ask again
      pendingWeeks: {},
      error: '',
      showFilters: false,
      showQuality: false,
      showDetails: false,
      streamFilter: [],
      categoryFilter: [],
      slotFilter: [],
      allergyFilter: [],
      // the id of a meal filter preset (v2/meal-filter) the meals must meet
      presetFilter: '',
      mealFilters: [],
      search: '',
      // the cloudflare image id of the selected photo (its url when it is not a cloudflare image)
      selectedKey: '',
      // a named variant ('large' to start), or 'flexible' for one built from imageOptions
      variantMode: 'large',
      imageOptions: emptyImageOptions(),
      isFullscreen: false,
      isZoomed: false,
      isDownloading: false,
      // the natural size of the large image once it has loaded, and the size of the box it is shown in
      imageSize: null,
      stageWidth: 0,
      stageHeight: 0,
    };
  },
  mounted() {
    this.syncToUrl({param: 'date', urlParam: 'date', initFromRoute: true});
    this.syncToUrl({param: 'weekCount', urlParam: 'weeks', initFromRoute: true, parseCallback: v => Number(v) || 1});
    this.syncToUrl({param: 'streamFilter', urlParam: 'stream', initFromRoute: true, parseCallback: toList});
    this.syncToUrl({param: 'categoryFilter', urlParam: 'category', initFromRoute: true, parseCallback: toList});
    this.syncToUrl({param: 'slotFilter', urlParam: 'slot', initFromRoute: true, parseCallback: toList});
    this.syncToUrl({param: 'allergyFilter', urlParam: 'allergy', initFromRoute: true, parseCallback: toList});
    this.syncToUrl({param: 'presetFilter', urlParam: 'preset', initFromRoute: true});
    this.syncToUrl({param: 'search', urlParam: 'q', initFromRoute: true});
    this.syncToUrl({param: 'selectedKey', urlParam: 'photo', initFromRoute: true});
    this.syncToUrl({param: 'variant', urlParam: 'variant', initFromRoute: true});
    this.restoreFilters();
    this.fetchStreams();
    this.fetchRestrictions();
    this.fetchNutrients();
    this.fetchMealFilters();
    this.fetchWeeks();
    // the photos get the whole window, as on Plating TV; the Filters dialog brings the menu back
    this.SET_HIDE_TOP_NAV({hideTopNav: true});
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    window.addEventListener('resize', this.measureStage);
  },
  beforeDestroy() {
    // the flag outlives the page; put the menu back for the next one
    if (this.hideTopNav) this.SET_HIDE_TOP_NAV({hideTopNav: false});
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    window.removeEventListener('resize', this.measureStage);
  },
  watch: {
    date() {
      this.fetchWeeks();
    },
    weekCount() {
      this.fetchWeeks();
      this.saveFilters();
    },
    streamFilter: 'saveFilters',
    categoryFilter: 'saveFilters',
    slotFilter: 'saveFilters',
    allergyFilter: 'saveFilters',
    presetFilter: 'saveFilters',
    variant: 'saveFilters',
    shownPhotos(photos) {
      // keep a selection the user can see
      if (photos.length && !photos.some(photo => this.keyOf(photo) === this.selectedKey)) {
        this.selectedKey = this.keyOf(photos[0]);
      }
    },
    selected(photo) {
      // the stage is only there with a photo to show
      if (photo) this.$nextTick(this.measureStage);
    },
    displayUrl() {
      this.imageSize = null;
    },
    imageOptions: {
      deep: true,
      handler(options) {
        // typing a resize option switches to a flexible variant; clearing them all does not
        if (flexibleVariant(options)) this.variantMode = 'flexible';
      },
    },
  },
  computed: {
    ...mapState(['restrictions', 'nutrients', 'hideTopNav']),
    ...mapGetters(['getStreamName']),
    isLoading() {
      return Object.keys(this.pendingWeeks).length > 0;
    },
    weekCountItems() {
      return Array.from({length: MAX_WEEKS}, (_, i) => ({value: i + 1, text: i === 0 ? '1 week' : `${i + 1} weeks`}));
    },
    /** the Sundays being shown, from the picked week on */
    weeks() {
      const start = moment(this.date).startOf('week');
      return Array.from({length: this.weekCount}, (_, i) => start.clone().add(i, 'weeks').format(moment.HTML5_FMT.DATE));
    },
    weeksWithoutMenu() {
      return this.weeks.filter(week => this.menusByWeek[week] === null);
    },
    photos() {
      return collectPhotos(this.weeks.map(week => ({date: week, menu: this.menusByWeek[week] || null})));
    },
    excludedTagIds() {
      return restrictedTagIds(this.restrictions, this.allergyFilter);
    },
    presetItems() {
      return this.mealFilters
        .filter(filter => filter.is_preset)
        .map(filter => ({value: filter.id, text: filter.title || filter.id}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    selectedPreset() {
      return this.mealFilters.find(filter => filter.id === this.presetFilter) || null;
    },
    /** the chosen preset as a check on each photo; a preset not (yet) loaded hides nothing */
    presetCheck() {
      if (!this.selectedPreset) return null;
      const nutrients = this.nutrients || {};
      return mealFilterCheck(this.selectedPreset, this.restrictions, id => (nutrients[id] || {}).nutrientsymbol);
    },
    shownPhotos() {
      return filterPhotos(this.photos, {
        streams: this.streamFilter,
        categories: this.categoryFilter,
        slots: this.slotFilter,
        search: this.search,
        excludeTagIds: this.excludedTagIds,
        mealFilter: this.presetCheck,
      });
    },
    thumbnailGroups() {
      const groups = new Map();
      for (const photo of this.shownPhotos) {
        const week = photo.weeks[0];
        if (!groups.has(week)) groups.set(week, {week, photos: []});
        groups.get(week).photos.push(photo);
      }
      return [...groups.values()];
    },
    streamItems() {
      return distinctValues(this.photos.map(photo => ({streams: visibleStreams(photo)})), 'streams')
        .map(stream => ({value: stream, text: this.streamLabel(stream)}));
    },
    categoryItems() {
      return distinctValues(this.photos, 'categories');
    },
    slotItems() {
      return PHOTO_SLOTS;
    },
    activeFilterCount() {
      return [this.streamFilter, this.categoryFilter, this.slotFilter, this.allergyFilter]
        .filter(list => list.length > 0).length + (`${this.search || ''}`.trim() ? 1 : 0) + (this.presetFilter ? 1 : 0);
    },
    /** the week(s) shown and each active filter, beside the Filters button */
    filterSummary() {
      const start = dateFormatted(this.weeks[0]);
      const span = this.weeks.length > 1 ? `${start} + ${this.weeks.length - 1} more weeks` : `Week of ${start}`;
      const parts = [span];
      if (this.streamFilter.length) parts.push(this.streamFilter.map(this.streamLabel).join(', '));
      if (this.categoryFilter.length) parts.push(this.categoryFilter.join(', '));
      if (this.slotFilter.length) parts.push(this.slotFilter.join(', '));
      // a restriction's own text already reads 'no beef'
      if (this.allergyFilter.length) parts.push(this.allergyFilter.map(this.allergyLabel).join(', '));
      if (this.presetFilter) parts.push(`preset ${this.selectedPreset ? (this.selectedPreset.title || this.selectedPreset.id) : this.presetFilter}`);
      if (`${this.search || ''}`.trim()) parts.push(`"${this.search.trim()}"`);
      parts.push(`${this.shownPhotos.length} of ${this.photos.length} photos`);
      return parts.join(' · ');
    },
    selectedIndex() {
      return this.shownPhotos.findIndex(photo => this.keyOf(photo) === this.selectedKey);
    },
    selected() {
      return this.shownPhotos[this.selectedIndex] || null;
    },
    /** the variant on the image url: a named one, or the resize options; settable from the url and the saved filters */
    variant: {
      get() {
        if (this.variantMode === 'flexible') {
          return flexibleVariant(this.imageOptions) || 'small';
        }
        return this.variantMode;
      },
      set(value) {
        if (isNamedVariant(value)) {
          this.imageOptions = emptyImageOptions();
          this.variantMode = value;
          return;
        }
        const options = parseFlexibleVariant(value);
        if (flexibleVariant(options)) {
          this.imageOptions = {...emptyImageOptions(), ...options};
          this.variantMode = 'flexible';
        }
      },
    },
    /** the selected photo at the chosen variant */
    displayUrl() {
      return this.selected ? cloudflareImageUrl(this.selected.url, this.variant) : '';
    },
    detailsLine() {
      return this.selected ? photoDetailsLine(this.selected, this.displayUrl) : '';
    },
    /** the named variant in use, or 'custom' for a flexible one */
    preset() {
      return this.variantMode === 'flexible' ? 'custom' : this.variantMode;
    },
    /**
     * the width of the image box: as large as fits the stage at the image's shape, so there are no
     * bars, and never larger than the image itself, so a small photo sits at its own size
     */
    imageBoxWidth() {
      if (!this.stageWidth || !this.stageHeight) return undefined;
      let width = Math.min(this.stageWidth, this.stageHeight * this.imageAspectRatio);
      if (this.imageSize) width = Math.min(width, this.imageSize.width);
      return Math.floor(width);
    },
    /** the loaded image's shape; the catalog's 3:2 until it has loaded */
    imageAspectRatio() {
      return this.imageSize ? this.imageSize.width / this.imageSize.height : 3 / 2;
    },
    /** 'large (2048×2048) · 1600×1067', on the photo; the size once the image has loaded */
    qualitySummary() {
      const preset = PRESETS.find(p => p.key === this.preset);
      const label = preset ? preset.label : this.preset;
      return this.imageSize ? `${label} · ${this.imageSize.width}×${this.imageSize.height}` : label;
    },
    /** what the url's variant does, in words, above the url in the Quality dialog */
    variantExplanation() {
      if (this.variantMode !== 'flexible') {
        const named = NAMED_VARIANTS.find(v => v.name === this.variantMode);
        const size = named ? `scaled to fit within ${named.size}` : 'a named variant of the account';
        const published = this.variantMode === 'small' ? '; this is the url the catalog publishes' : '';
        return `The url ends in the named variant "${this.variantMode}": the image ${size}, never enlarged${published}.`;
      }
      const o = this.imageOptions;
      const parts = [];
      if (o.width && o.height) parts.push(`fit within ${o.width}×${o.height}px`);
      else if (o.width) parts.push(`${o.width}px wide`);
      else if (o.height) parts.push(`${o.height}px tall`);
      parts.push(`fit: ${o.fit || 'scale-down'}${o.fit ? '' : ' (never enlarged)'}`);
      parts.push(`quality ${o.quality || '85 (default)'}`);
      parts.push(`format ${o.format || 'auto (the best the browser accepts)'}`);
      if (o.sharpen) parts.push(`sharpen ${o.sharpen}`);
      if (o.blur) parts.push(`blur ${o.blur}`);
      if (o.dpr) parts.push(`${o.dpr}× device pixel ratio`);
      return `The url ends in resize options, so Cloudflare makes this version on the fly: ${parts.join(', ')}.`;
    },
    /** the one-click presets on the button row: the named variants */
    quickPresets() {
      return PRESETS.filter(p => p.variant);
    },
    fitItems() {
      return FIT_OPTIONS;
    },
    formatItems() {
      return FORMAT_OPTIONS;
    },
  },
  methods: {
    ...mapActions(['fetchStreams', 'fetchRestrictions', 'fetchNutrients']),
    ...mapMutations(['SET_HIDE_TOP_NAV']),
    dateFormatted,
    formatCurrency,
    thumbnailUrl,
    visibleStreams,
    isSunday(d) {
      return moment(d).day() === 0;
    },
    shortDate(date) {
      return date && date !== 'any' ? moment(date).format('ddd D MMM') : '';
    },
    streamLabel(stream) {
      if (stream === 'chefs_choice') return 'Chef\'s Choice';
      const name = this.getStreamName(stream);
      return name && name !== stream ? `${name} (${stream})` : stream;
    },
    allergyLabel(id) {
      const restriction = (this.restrictions || {})[id];
      return (restriction && restriction.text) || id;
    },
    keyOf(photo) {
      const image = parseCloudflareImage(photo.url);
      return image ? image.imageId : photo.url;
    },
    moveWeek(weeks) {
      this.date = moment(this.date).startOf('week').add(weeks, 'weeks').format(moment.HTML5_FMT.DATE);
    },
    /** the filters from the last visit, for any the url does not set */
    restoreFilters() {
      const saved = loadSavedFilters();
      const query = this.$route.query;
      for (const [param, urlParam] of Object.entries(FILTER_PARAMS)) {
        if (saved[param] !== undefined && query[urlParam] === undefined) {
          this[param] = saved[param];
        }
      }
    },
    saveFilters() {
      const filters = Object.fromEntries(Object.keys(FILTER_PARAMS).map(param => [param, this[param]]));
      try {
        localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
      } catch (e) {
        // nothing to do: the filters are still in the url
      }
    },
    clearFilters() {
      this.streamFilter = [];
      this.categoryFilter = [];
      this.slotFilter = [];
      this.allergyFilter = [];
      this.presetFilter = '';
      this.search = '';
    },
    fetchMealFilters() {
      return api.get('v2/meal-filter')
        .then(({data}) => {
          this.mealFilters = Array.isArray(data) ? data : [];
        })
        .catch(e => {
          this.error = `Could not load the meal presets: ${(e && e.message) || e}`;
        });
    },
    fetchWeeks() {
      const missing = this.weeks.filter(week => this.menusByWeek[week] === undefined && !this.pendingWeeks[week]);
      if (!missing.length) return Promise.resolve();
      this.error = '';
      for (const week of missing) this.$set(this.pendingWeeks, week, true);
      return Promise.all(missing.map(week => api.get(`v2/menu/${week}`)
        .then(({data}) => {
          this.$set(this.menusByWeek, week, data || null);
        })
        .catch(e => {
          this.error = `Could not load the menu for ${week}: ${(e && e.message) || e}`;
        })
        .finally(() => this.$delete(this.pendingWeeks, week))));
    },
    select(photo) {
      this.selectedKey = this.keyOf(photo);
    },
    step(by) {
      const next = this.shownPhotos[this.selectedIndex + by];
      if (next) this.select(next);
    },
    onKeydown(event) {
      if (this.showFilters || this.showQuality) return;
      const tag = event.target && event.target.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    },
    fullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
        return;
      }
      const stage = this.$refs.stage;
      if (!stage) return;
      const request = stage.requestFullscreen || stage.webkitRequestFullscreen;
      if (request) request.call(stage);
    },
    /** the app bar (the yellow one off production) */
    toggleTopNav() {
      this.SET_HIDE_TOP_NAV({hideTopNav: !this.hideTopNav});
    },
    onLargeLoad() {
      const image = this.$refs.large && this.$refs.large.image;
      if (image && image.naturalWidth) {
        this.imageSize = {width: image.naturalWidth, height: image.naturalHeight};
      }
      this.measureStage();
    },
    measureStage() {
      const stage = this.$refs.stage;
      this.stageWidth = stage ? stage.clientWidth : 0;
      this.stageHeight = stage ? stage.clientHeight : 0;
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
      this.$nextTick(this.measureStage);
    },
    applyPreset(key) {
      const preset = PRESETS.find(p => p.key === key);
      if (!preset) return;
      if (preset.variant) {
        this.imageOptions = emptyImageOptions();
        this.variantMode = preset.variant;
      } else {
        this.variantMode = 'flexible';
      }
    },
    async copy(text, message) {
      try {
        await navigator.clipboard.writeText(text);
        this.$store.commit('showSnackbar', {message, color: 'success', timeout: 1500});
      } catch (e) {
        this.$store.commit('showSnackbar', {message: `Could not copy: ${e.message || e}`, color: 'error'});
      }
    },
    /** save the photo at the chosen quality; when the image cannot be fetched, it opens in a tab instead */
    async download() {
      const url = this.displayUrl;
      const photo = this.selected;
      this.isDownloading = true;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = photoFileName(photo, blob.type);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      } catch (e) {
        window.open(url, '_blank', 'noopener');
        this.$store.commit('showSnackbar', {message: `Could not download the image (${e.message || e}); opened it in a new tab`, color: 'warning'});
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style scoped>
.week-field {
  max-width: 150px;
}

.weeks-select {
  max-width: 120px;
}

.quick-presets {
  flex: 0 0 auto;
}

.button-row {
  overflow-x: auto;
}

/* the buttons stay at the foot of the view, the details folded away under them */
.stage-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #fff;
  padding: 8px 0 20px;
}

.stage-details {
  max-height: 50vh;
  overflow-y: auto;
}

.stage {
  /* a box the height of the view less the footer, so the footer sits at the foot without scrolling */
  height: calc(100vh - 96px);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
  border-radius: 4px;
  outline: none;
}

/* the image box is sized by imageBoxWidth; it must not grow to the stage width */
.stage-image {
  flex: 0 0 auto;
}

.stage-empty {
  color: #999;
  background: #f5f5f5;
}


.stage-image {
  cursor: zoom-in;
}

.stage-zoomed .stage-image {
  cursor: zoom-out;
}

/* the middle of the photo at twice the size, clipped to the same box */
.stage-zoomed .stage-image >>> .v-image__image {
  transform: scale(2);
  transform-origin: center;
}

.stage-fullscreen {
  width: 100vw;
  height: 100vh;
  border-radius: 0;
  background: #000;
}

.stage-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.6);
}

.stage-prev {
  left: 8px;
}

.stage-next {
  right: 8px;
}

.stage-quality {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6) !important;
  color: #fff !important;
}

.stage-title {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 40%;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.stage-title-chip {
  background: rgba(0, 0, 0, 0.6) !important;
  color: #fff !important;
}

.stage-tools {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  gap: 4px;
}

.stage-tool {
  background: rgba(0, 0, 0, 0.6);
  color: #fff !important;
}

.stage-tool-on {
  background: #1976d2;
}

.stage-caption {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  text-align: center;
  color: #fff;
  text-shadow: 0 0 6px #000;
}

.thumb {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 4px;
  padding: 2px;
}

.thumb:hover {
  border-color: #bbb;
}

.thumb-selected {
  border-color: #1976d2;
}

/* the thumbnails scroll on their own, the height of the view, while the photo stays put */
@media (min-width: 960px) {
  .thumb-pane {
    position: sticky;
    top: 0;
    max-height: 100vh;
    overflow-y: auto;
    align-self: flex-start;
  }
}

.thumb-row {
  margin-top: 0;
  margin-bottom: 0;
}

.thumb-col {
  padding: 2px 4px;
}

.thumb-chips {
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 4px;
  display: flex;
  justify-content: space-between;
  gap: 4px;
}

.thumb-chip {
  background: rgba(0, 0, 0, 0.6) !important;
  color: #fff !important;
}

/* the meal name on two lines, clipped after that */
.thumb-caption {
  font-size: 12px;
  line-height: 1.3;
  height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
